import { Component, input, output, computed, ChangeDetectionStrategy } from '@angular/core';
import { ServiceLight } from '../../models/service-light-model';

@Component({
  selector: 'app-service-selector',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isLoading()) {
      <div class="grid md:grid-cols-2 gap-4">
        @for (i of [1,2,3,4]; track i) {
          <div class="p-6 animate-pulse border rounded-xl bg-white">
            <div class="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
            <div class="h-4 bg-slate-200 rounded w-full mb-2"></div>
            <div class="h-4 bg-slate-200 rounded w-2/3"></div>
          </div>
        }
      </div>
    } @else {
      <div class="space-y-6">
        @for (group of groupedServices(); track group.category) {
          <div>
            <h3 class="text-lg font-semibold text-[#121219] mb-3 capitalize flex items-center gap-2">
              <span class="text-2xl">{{ getCategoryIcon(group.category) }}</span>
              {{ getCategoryName(group.category) }} услуги
            </h3>

            <div class="grid md:grid-cols-2 gap-4">
              @for (service of group.services; track service.id) {
                <div 
                  (click)="toggleService.emit(service.id)"
                  class="p-6 cursor-pointer transition-all duration-200 border-2 rounded-2xl"
                  [class]="getCardClass(service.id, group.category)">
                  
                  <div class="flex items-start justify-between mb-3">
                    <h4 class="font-semibold text-lg" 
                        [class]="isSelected(service.id) ? 'text-white' : 'text-[#121219]'">
                      {{ service.serviceName }}
                    </h4>
                    @if (isSelected(service.id)) {
                      <div class="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                        <lucide-icon name="check" class="w-4 h-4 text-white"></lucide-icon>
                      </div>
                    }
                  </div>
                  
                  <p class="text-sm mb-4" [class]="isSelected(service.id) ? 'text-white/90' : 'text-[#67686C]'">
                    {{ service.serviceDescription }}
                  </p>

                  <div class="flex items-center justify-between">
                    <span class="px-2.5 py-0.5 text-xs font-semibold rounded-full flex items-center gap-1"
                          [class]="isSelected(service.id) ? 'bg-white/20 text-white' : 'bg-slate-100 text-[#121219]'">
                      <lucide-icon name="clock" class="w-3 h-3"></lucide-icon>
                      {{ formatDuration(service.serviceDuration) }}
                    </span>
                    <div [class]="isSelected(service.id) ? 'text-white' : 'text-[#BF7A7A]'">
                      <div class="text-right">
                        <div class="font-bold text-lg">от €{{ service.price }}</div>
                        <div class="text-xs opacity-75">от {{ convertToBGN(service.price) }} лв</div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        }

        @if (selectedIds().length > 0) {
          <div class="p-6 bg-gradient-to-r from-[#BF7A7A] to-[#6E7E97] text-white border-0 shadow-xl rounded-2xl">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-white/80 mb-1">Общо избрани</p>
                <p class="text-2xl font-bold flex items-center gap-2">
                 от €{{ totals().priceEur.toFixed(2) }}
                </p>
                <p class="text-sm text-white/90 mt-1">{{ totals().priceBgn.toFixed(2) }} лв</p>
              </div>
              <div class="text-right">
                <p class="text-sm text-white/80 mb-1">Продължителност</p>
                <p class="text-2xl font-bold flex items-center gap-2">
                  <lucide-icon name="clock" class="w-6 h-6"></lucide-icon>
                  {{ formatDuration(totals().duration) }}
                </p>
              </div>
            </div>
          </div>
        }
      </div>
    }
  `
})
export class ServiceSelectorComponent {
  services = input.required<ServiceLight[]>();
  selectedIds = input<number[]>([]); 
  isLoading = input<boolean>(false);
  toggleService = output<number>();

  private categoryColors: Record<string, string> = {
    interior: 'from-[#6E7E97] to-[#7E8E8B]',
    exterior: 'from-[#BF7A7A] to-[#6E7E97]',
    premium: 'from-[#633B3C] to-[#BF7A7A]',
    addon: 'from-[#7E8E8B] to-[#6E7E97]',
    general: 'from-[#BF7A7A] to-[#6E7E97]'
  };

  private categoryIcons: Record<string, string> = {
    interior: '🧽', exterior: '🚗', premium: '✨', addon: '🔧', general: '✨'
  };

  private categoryNames: Record<string, string> = {
    interior: 'Интериорни', exterior: 'Екстериорни', premium: 'Премиум', addon: 'Допълнителни', general: 'Основни'
  };

  groupedServices = computed(() => {
    const groups: Record<string, ServiceLight[]> = {};
    this.services().forEach(service => {
      const name = service.serviceName.toLowerCase();
      let category = 'general';
      if (name.includes('interior')) category = 'interior';
      else if (name.includes('exterior')) category = 'exterior';
      else if (name.includes('premium')) category = 'premium';
      if (!groups[category]) groups[category] = [];
      groups[category].push(service);
    });
    return Object.keys(groups).map(key => ({ category: key, services: groups[key] }));
  });

  totals = computed(() => {
    const currentSelectedIds = this.selectedIds();
    const selected = this.services().filter(s => currentSelectedIds.includes(s.id));
    const priceEur = selected.reduce((sum, s) => sum + (Number(s.price) || 0), 0);
    const duration = selected.reduce((sum, s) => sum + (s.serviceDuration || 0), 0);
    return { priceEur, priceBgn: priceEur * 1.95, duration };
  });

  isSelected(id: number) { return this.selectedIds().includes(id); }
  getCategoryIcon(cat: string) { return this.categoryIcons[cat] || '✨'; }
  getCategoryName(cat: string) { return this.categoryNames[cat] || 'Услуги'; }

  getCardClass(id: number, category: string) {
    const isSelected = this.isSelected(id);
    const colors = this.categoryColors[category] || this.categoryColors['general'];
    return isSelected 
      ? `bg-gradient-to-br ${colors} text-white border-transparent shadow-lg scale-[1.02]`
      : 'bg-white border-slate-200 hover:border-[#BF7A7A]/50 hover:shadow-md';
  }

  formatDuration(totalMinutes: number) {
    if (!totalMinutes) return '0 мин.';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes} мин.`;
    if (minutes === 0) return `${hours} ч.`;
    return `${hours} ч. ${minutes} мин.`;
  }

  convertToBGN(val: string | number) {
    const num = Number(val) || 0;
    return (num * 1.95).toFixed(2);
  }
}